import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid'
import {
  Button,
  Input,
  Text,
  LoadingSpinner,
  Select,
  SortableTableHeader,
} from '../../components/ui'
import { HorizontalTableScroll } from './HorizontalTableScroll'
import AnaliseComparativaComissaoModal from './AnaliseComparativaComissaoModal'
import { financeiroService } from '../../services/financeiro'
import { financeiroExtratosContaService } from '../../services/financeiroExtratosConta'
import { formatMoneyBr } from '../../utils/financeiroComissoesImport'
import type {
  FinanceiroAnaliseComparativaFilters,
  FinanceiroAnaliseComparativaLinha,
} from '../../types/financeiro'
import type { FinanceiroPelStatementType } from '../../types/financeiroExtratosConta'
import {
  CARD_CLASS,
  ERROR_BANNER_CLASS,
  FIELD_LABEL_CLASS,
  FILTER_CARD_CLASS,
  PAGINATION_BTN_CLASS,
  PASI_ANALISE_CNPJ,
  PASI_ANALISE_CONTA,
  PASI_ANALISE_IMPOSTO_PCT,
  PASI_ANALISE_MARGEM_PCT,
  PASI_ANALISE_PAGE_SIZE,
  PASI_ANALISE_PAGE_SIZE_OPTIONS,
  PASI_ANALISE_STATUS_RECIBO,
  PASI_ANALISE_TIPO_EXTRATO,
  formatDatePtBr,
  paginationPageClass,
} from './financeiroPelUi'

type DiffTone = 'equal' | 'lower' | 'higher' | 'neutral'

type SortKey =
  | 'dia'
  | 'totalExtrato'
  | 'totalComissao'
  | 'comissaoSemImpostos'
  | 'diferencaValor'
  | 'diferencaPct'

type PageSizeOption = (typeof PASI_ANALISE_PAGE_SIZE_OPTIONS)[number] | 'all'

const COLUMN_DEFS: { key: SortKey; label: string }[] = [
  { key: 'dia', label: 'Data' },
  { key: 'totalExtrato', label: 'Valor do extrato (R$)' },
  { key: 'totalComissao', label: 'Comissão Icatu (R$)' },
  { key: 'comissaoSemImpostos', label: 'Comissão Icatu - % (R$)' },
  { key: 'diferencaValor', label: 'Diferença (R$)' },
  { key: 'diferencaPct', label: 'Diferença (%)' },
]

const COMPACT_INPUT_CLASS = '!w-[calc(8rem/3)] !min-w-0 !px-1 !py-0.5 text-center'
const CELL_CLASS = 'whitespace-nowrap px-3 py-1 text-sm text-gray-900 dark:text-gray-100'
const CELL_RIGHT_CLASS = `${CELL_CLASS} text-right`
const FOOT_CELL_CLASS =
  'whitespace-nowrap px-3 py-1 text-sm font-semibold text-gray-900 dark:text-white'
const FOOT_CELL_RIGHT_CLASS = `${FOOT_CELL_CLASS} text-right`

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  ) {
    return (err as { message: string }).message
  }
  return fallback
}

function parsePercentInput(raw: string): number | null {
  const trimmed = raw.trim().replace('%', '').replace(/\s/g, '')
  if (!trimmed) return null
  const normalized = trimmed.replace(',', '.')
  const value = Number(normalized)
  return Number.isFinite(value) ? value : null
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}

function isDisplayedZero(value: number): boolean {
  return round2(value) === 0
}

function EmptyAmount() {
  return <span className="text-gray-500 dark:text-gray-400">—</span>
}

/** Cabeçalho da coluna líquida, com o % médio de impostos informado na tela. */
function comissaoLiquidaColumnLabel(impostoPctRaw: string): React.ReactNode {
  const pct = impostoPctRaw.trim().replace(/%$/, '') || '—'
  return (
    <>
      Comissão Icatu - {pct}%<br />
      de Impostos (R$)
    </>
  )
}

/** Comissão líquida: valor total menos o percentual médio de impostos informado. */
function comissaoSemImpostos(comissao: number, impostoPct: number | null): number | null {
  if (impostoPct == null || !Number.isFinite(comissao)) return null
  return comissao * (1 - impostoPct / 100)
}

/**
 * Percentual da variação: (extrato − (comissão − impostos)) / (comissão − impostos) × 100.
 * Base 0: 0% se extrato também 0; ±100% conforme o sinal da diferença.
 */
function diffPercent(comissaoLiquida: number, extrato: number): number | null {
  if (!Number.isFinite(comissaoLiquida) || !Number.isFinite(extrato)) return null
  if (comissaoLiquida === 0) {
    if (extrato === 0) return 0
    return extrato - comissaoLiquida < 0 ? -100 : 100
  }
  return ((extrato - comissaoLiquida) / comissaoLiquida) * 100
}

/**
 * Classifica a variação com margem ±:
 * |diffPct| ≤ margem → igual; abaixo → menor; acima → maior.
 */
function toneForPercent(
  diffPct: number | null,
  impostoPct: number | null,
  margemPct: number | null
): DiffTone {
  if (diffPct == null || impostoPct == null || margemPct == null) return 'neutral'
  const a = round2(diffPct)
  const margin = Math.abs(round2(margemPct))
  if (Math.abs(a) <= margin) return 'equal'
  if (a < 0) return 'lower'
  return 'higher'
}

function rowToneClass(tone: DiffTone): string {
  switch (tone) {
    case 'equal':
      return 'bg-green-50 hover:bg-green-100 dark:bg-green-950/40 dark:hover:bg-green-900/50'
    case 'lower':
      return 'bg-red-50 hover:bg-red-100 dark:bg-red-950/40 dark:hover:bg-red-900/50'
    case 'higher':
      return 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/50'
    default:
      return 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
  }
}

function toneTextClass(tone: DiffTone): string {
  switch (tone) {
    case 'equal':
      return 'text-green-600 dark:text-green-400 font-semibold'
    case 'lower':
      return 'text-red-600 dark:text-red-400 font-semibold'
    case 'higher':
      return 'text-blue-600 dark:text-blue-400 font-semibold'
    default:
      return 'text-gray-900 dark:text-gray-100'
  }
}

function formatPercentBr(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`
}

function formatSharePct(count: number, total: number): string {
  if (total <= 0) return '0%'
  const pct = (count / total) * 100
  return `${pct.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`
}

function DiffToneIcon({ tone }: { tone: DiffTone }) {
  const iconClass = 'h-4 w-4 shrink-0'
  switch (tone) {
    case 'equal':
      return (
        <CheckCircleIcon
          className={`${iconClass} text-green-600 dark:text-green-400`}
          aria-label="Diferença igual"
          title="igual"
        />
      )
    case 'lower':
      return (
        <ArrowDownCircleIcon
          className={`${iconClass} text-red-600 dark:text-red-400`}
          aria-label="Diferença menor"
          title="menor"
        />
      )
    case 'higher':
      return (
        <ArrowUpCircleIcon
          className={`${iconClass} text-blue-600 dark:text-blue-400`}
          aria-label="Diferença maior"
          title="maior"
        />
      )
    default:
      return null
  }
}

function DiffPercentCell({
  diffPct,
  tone,
}: {
  diffPct: number | null
  tone: DiffTone
}) {
  const showDash = diffPct == null || !Number.isFinite(diffPct) || isDisplayedZero(diffPct)
  return (
    <span
      className={`inline-flex items-center justify-end gap-1.5 ${showDash ? '' : toneTextClass(tone)}`}
    >
      {showDash ? <EmptyAmount /> : <span className="tabular-nums">{formatPercentBr(diffPct)}</span>}
      <DiffToneIcon tone={tone} />
    </span>
  )
}

function DiffValorCell({
  diff,
  tone,
  renderMoney,
}: {
  diff: number | null
  tone: DiffTone
  renderMoney: (value: number) => React.ReactNode
}) {
  if (diff == null || isDisplayedZero(diff)) {
    return <EmptyAmount />
  }
  return <span className={toneTextClass(tone)}>{renderMoney(diff)}</span>
}

type RowMetrics = {
  comissaoLiquida: number | null
  diff: number | null
  diffPct: number | null
  tone: DiffTone
}

function rowMetrics(
  comissao: number,
  extrato: number,
  impostoPct: number | null,
  margemPct: number | null
): RowMetrics {
  const comissaoLiquida = comissaoSemImpostos(comissao, impostoPct)
  if (comissaoLiquida == null) {
    return { comissaoLiquida: null, diff: null, diffPct: null, tone: 'neutral' }
  }
  const diff = extrato - comissaoLiquida
  const diffPct = diffPercent(comissaoLiquida, extrato)
  return {
    comissaoLiquida,
    diff,
    diffPct,
    tone: toneForPercent(diffPct, impostoPct, margemPct),
  }
}

const AnaliseComparativaTab: React.FC = () => {
  const [rows, setRows] = useState<FinanceiroAnaliseComparativaLinha[]>([])
  const [accounts, setAccounts] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)

  const [cpfCnpj, setCpfCnpj] = useState(PASI_ANALISE_CNPJ)
  const [statusRecibo, setStatusRecibo] = useState(PASI_ANALISE_STATUS_RECIBO)
  const [dataPagamentoEfetivoDe, setDataPagamentoEfetivoDe] = useState('')
  const [dataPagamentoEfetivoAte, setDataPagamentoEfetivoAte] = useState('')
  const [extratoDataDe, setExtratoDataDe] = useState('')
  const [extratoDataAte, setExtratoDataAte] = useState('')
  const [account, setAccount] = useState(PASI_ANALISE_CONTA)
  const [identification, setIdentification] = useState('')
  const [type, setType] = useState<'' | FinanceiroPelStatementType>(PASI_ANALISE_TIPO_EXTRATO)
  const [impostoPctRaw, setImpostoPctRaw] = useState(PASI_ANALISE_IMPOSTO_PCT)
  const [margemPctRaw, setMargemPctRaw] = useState(PASI_ANALISE_MARGEM_PCT)
  const [showEqual, setShowEqual] = useState(true)
  const [showLower, setShowLower] = useState(true)
  const [showHigher, setShowHigher] = useState(true)

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSizeOption, setPageSizeOption] = useState<PageSizeOption>(PASI_ANALISE_PAGE_SIZE)
  const [sortBy, setSortBy] = useState<SortKey>('dia')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [appliedComissaoFilters, setAppliedComissaoFilters] = useState<{
    cpfCnpj: string | null
    tipoComissao: string | null
    statusRecibo: string | null
  }>({ cpfCnpj: null, tipoComissao: null, statusRecibo: null })
  const [detailDia, setDetailDia] = useState<{
    dia: string
    totalComissao: number
  } | null>(null)
  const requestIdRef = useRef(0)

  const impostoPct = parsePercentInput(impostoPctRaw)
  const margemPct = parsePercentInput(margemPctRaw)

  const toneShare = useMemo(() => {
    let equal = 0
    let lower = 0
    let higher = 0
    for (const row of rows) {
      const { tone } = rowMetrics(row.totalComissao, row.totalExtrato, impostoPct, margemPct)
      if (tone === 'equal') equal += 1
      else if (tone === 'lower') lower += 1
      else if (tone === 'higher') higher += 1
    }
    const total = equal + lower + higher
    return {
      equal: formatSharePct(equal, total),
      lower: formatSharePct(lower, total),
      higher: formatSharePct(higher, total),
    }
  }, [rows, impostoPct, margemPct])

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const { tone } = rowMetrics(row.totalComissao, row.totalExtrato, impostoPct, margemPct)
      if (tone === 'equal') return showEqual
      if (tone === 'lower') return showLower
      if (tone === 'higher') return showHigher
      return showEqual && showLower && showHigher
    })
  }, [rows, impostoPct, margemPct, showEqual, showLower, showHigher])

  const sortedRows = useMemo(() => {
    const list = [...filteredRows]
    const dir = sortOrder === 'asc' ? 1 : -1
    list.sort((a, b) => {
      if (sortBy === 'dia') {
        if (a.dia < b.dia) return -dir
        if (a.dia > b.dia) return dir
        return 0
      }

      const ma = rowMetrics(a.totalComissao, a.totalExtrato, impostoPct, margemPct)
      const mb = rowMetrics(b.totalComissao, b.totalExtrato, impostoPct, margemPct)

      let va: number | null
      let vb: number | null
      switch (sortBy) {
        case 'totalExtrato':
          va = a.totalExtrato
          vb = b.totalExtrato
          break
        case 'totalComissao':
          va = a.totalComissao
          vb = b.totalComissao
          break
        case 'comissaoSemImpostos':
          va = ma.comissaoLiquida
          vb = mb.comissaoLiquida
          break
        case 'diferencaValor':
          va = ma.diff
          vb = mb.diff
          break
        case 'diferencaPct':
          va = ma.diffPct
          vb = mb.diffPct
          break
        default:
          return 0
      }

      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1
      if (va < vb) return -dir
      if (va > vb) return dir
      return 0
    })
    return list
  }, [filteredRows, sortBy, sortOrder, impostoPct, margemPct])

  const totalComissao = useMemo(
    () => filteredRows.reduce((sum, row) => sum + (row.totalComissao || 0), 0),
    [filteredRows]
  )
  const totalExtrato = useMemo(
    () => filteredRows.reduce((sum, row) => sum + (row.totalExtrato || 0), 0),
    [filteredRows]
  )
  const totalMetrics = useMemo(
    () => rowMetrics(totalComissao, totalExtrato, impostoPct, margemPct),
    [totalComissao, totalExtrato, impostoPct, margemPct]
  )

  const effectivePageSize =
    pageSizeOption === 'all'
      ? Math.max(sortedRows.length, 1)
      : pageSizeOption

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / effectivePageSize))
  const startIndex = sortedRows.length === 0 ? 0 : (currentPage - 1) * effectivePageSize
  const endIndex = Math.min(startIndex + effectivePageSize, sortedRows.length)
  const pageRows = sortedRows.slice(startIndex, endIndex)

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(sortedRows.length / effectivePageSize))
    if (currentPage > pages) setCurrentPage(pages)
  }, [sortedRows.length, currentPage, effectivePageSize])

  const loadAccounts = useCallback(async () => {
    try {
      const list = await financeiroExtratosContaService.listStatementFilterAccounts()
      setAccounts(list)
    } catch (err) {
      console.error(err)
    }
  }, [])

  const loadRows = useCallback(async (filters: FinanceiroAnaliseComparativaFilters) => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    setCurrentPage(1)
    setDetailDia(null)
    try {
      const result = await financeiroService.listAnaliseComparativa(filters)
      if (requestId !== requestIdRef.current) return
      setRows(result.rows)
      setAppliedComissaoFilters({
        cpfCnpj: filters.cpfCnpj?.trim() || null,
        tipoComissao: filters.tipoComissao?.trim() || null,
        statusRecibo: filters.statusRecibo?.trim() || null,
      })
      setHasLoaded(true)
    } catch (err) {
      if (requestId !== requestIdRef.current) return
      console.error(err)
      setError(errorMessage(err, 'Erro ao carregar a análise comparativa.'))
      setRows([])
      setHasLoaded(true)
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadAccounts()
  }, [loadAccounts])

  const buildFilters = (): FinanceiroAnaliseComparativaFilters => ({
    cpfCnpj: cpfCnpj.trim() || null,
    statusRecibo: statusRecibo.trim() || null,
    dataPagamentoEfetivoDe: dataPagamentoEfetivoDe || null,
    dataPagamentoEfetivoAte: dataPagamentoEfetivoAte || null,
    extratoDataDe: extratoDataDe || null,
    extratoDataAte: extratoDataAte || null,
    account: account || null,
    identification: identification.trim() || null,
    type: type || null,
  })

  const applyFilters = () => {
    const hasComissao = Boolean(dataPagamentoEfetivoDe || dataPagamentoEfetivoAte)
    const hasExtrato = Boolean(extratoDataDe || extratoDataAte)
    if (!hasComissao && !hasExtrato) {
      setError(
        'Informe o período do Pagamento Efetivo e/ou o período do extrato para executar a análise.'
      )
      setRows([])
      setHasLoaded(false)
      return
    }
    if (hasComissao && (!dataPagamentoEfetivoDe || !dataPagamentoEfetivoAte)) {
      setError('Informe a data inicial e a data final do Pagamento Efetivo.')
      setRows([])
      setHasLoaded(false)
      return
    }
    if (hasExtrato && (!extratoDataDe || !extratoDataAte)) {
      setError('Informe a data inicial e a data final do extrato.')
      setRows([])
      setHasLoaded(false)
      return
    }
    void loadRows(buildFilters())
  }

  const clearFilters = () => {
    requestIdRef.current += 1
    setCpfCnpj(PASI_ANALISE_CNPJ)
    setStatusRecibo(PASI_ANALISE_STATUS_RECIBO)
    setDataPagamentoEfetivoDe('')
    setDataPagamentoEfetivoAte('')
    setExtratoDataDe('')
    setExtratoDataAte('')
    setAccount(PASI_ANALISE_CONTA)
    setIdentification('')
    setType(PASI_ANALISE_TIPO_EXTRATO)
    setImpostoPctRaw(PASI_ANALISE_IMPOSTO_PCT)
    setMargemPctRaw(PASI_ANALISE_MARGEM_PCT)
    setPageSizeOption(PASI_ANALISE_PAGE_SIZE)
    setCurrentPage(1)
    setRows([])
    setAppliedComissaoFilters({ cpfCnpj: null, tipoComissao: null, statusRecibo: null })
    setDetailDia(null)
    setError(null)
    setHasLoaded(false)
    setLoading(false)
  }

  const toggleSort = (key: string) => {
    const sortKey = key as SortKey
    if (sortBy === sortKey) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(sortKey)
      setSortOrder(sortKey === 'dia' ? 'asc' : 'desc')
    }
    setCurrentPage(1)
  }

  const renderMoney = (value: number) =>
    isDisplayedZero(value) ? (
      <EmptyAmount />
    ) : (
      <span className="tabular-nums">{formatMoneyBr(value)}</span>
    )

  return (
    <div className="space-y-6">
      <Text size="sm" color="muted">
        Comparação das comissões informadas pela Icatu com os extratos bancários do PASI
      </Text>
      {error && <div className={ERROR_BANNER_CLASS}>{error}</div>}

      <form
        className={FILTER_CARD_CLASS}
        onSubmit={(event) => {
          event.preventDefault()
          applyFilters()
        }}
      >
        <Text size="sm" className="mb-3 font-medium text-gray-800 dark:text-gray-200">
          Extrato Comissões Icatu
        </Text>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>CPF/CNPJ</label>
            <Input
              className="w-full"
              value={cpfCnpj}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCpfCnpj(e.target.value)}
              placeholder="Com ou sem máscara"
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Status_Recibo</label>
            <Input
              className="w-full"
              value={statusRecibo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStatusRecibo(e.target.value)}
              placeholder="Status do recibo"
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Pagamento efetivo de *</label>
            <Input
              type="date"
              className="w-full min-w-0"
              value={dataPagamentoEfetivoDe}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value
                setDataPagamentoEfetivoDe(value)
                setExtratoDataDe(value)
              }}
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Pagamento efetivo até *</label>
            <Input
              type="date"
              className="w-full min-w-0"
              value={dataPagamentoEfetivoAte}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value
                setDataPagamentoEfetivoAte(value)
                setExtratoDataAte(value)
              }}
            />
          </div>
        </div>

        <Text size="sm" className="mb-3 mt-6 font-medium text-gray-800 dark:text-gray-200">
          Extratos por conta
        </Text>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Extrato de * (pasta ou data/hora)</label>
            <Input
              type="date"
              className="w-full min-w-0"
              value={extratoDataDe}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setExtratoDataDe(e.target.value)
              }
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Extrato até *</label>
            <Input
              type="date"
              className="w-full min-w-0"
              value={extratoDataAte}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setExtratoDataAte(e.target.value)
              }
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Conta</label>
            <Select
              className="w-full"
              value={account}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAccount(e.target.value)}
            >
              <option value="">Todas</option>
              {(account && !accounts.includes(account) ? [account, ...accounts] : accounts).map(
                (acc) => (
                  <option key={acc} value={acc}>
                    {acc}
                  </option>
                )
              )}
            </Select>
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Identificação</label>
            <Input
              className="w-full"
              value={identification}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setIdentification(e.target.value)
              }
              placeholder="Contém..."
            />
          </div>
          <div className="min-w-0">
            <label className={FIELD_LABEL_CLASS}>Tipo</label>
            <Select
              className="w-full"
              value={type}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setType(e.target.value as '' | FinanceiroPelStatementType)
              }
            >
              <option value="">Todos</option>
              <option value="debit">Débito</option>
              <option value="credit">Crédito</option>
            </Select>
          </div>
          <div className="flex min-w-0 flex-wrap items-end gap-2">
            <Button type="submit" disabled={loading}>
              Buscar
            </Button>
            <Button type="button" variant="outline" onClick={clearFilters} disabled={loading}>
              Limpar
            </Button>
          </div>
        </div>
      </form>

      <div className={CARD_CLASS}>
        <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="flex min-w-0 items-center gap-2">
            <label className="whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300">
              % médio de impostos
            </label>
            <Input
              size="sm"
              className={COMPACT_INPUT_CLASS}
              value={impostoPctRaw}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setImpostoPctRaw(e.target.value)
              }
              placeholder="6,15"
              inputMode="decimal"
            />
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <label className="whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300">
              Margem de dif. aceitável
            </label>
            <Input
              size="sm"
              className={COMPACT_INPUT_CLASS}
              value={margemPctRaw}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setMargemPctRaw(e.target.value)
              }
              placeholder="0"
              inputMode="decimal"
            />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <span className="whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300">
              Diferença
            </span>
            <label className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800"
                checked={showEqual}
                onChange={(e) => {
                  setShowEqual(e.target.checked)
                  setCurrentPage(1)
                }}
              />
              <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
              igual ({toneShare.equal})
            </label>
            <label className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 dark:border-gray-600 dark:bg-gray-800"
                checked={showLower}
                onChange={(e) => {
                  setShowLower(e.target.checked)
                  setCurrentPage(1)
                }}
              />
              <ArrowDownCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
              menor ({toneShare.lower})
            </label>
            <label className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                checked={showHigher}
                onChange={(e) => {
                  setShowHigher(e.target.checked)
                  setCurrentPage(1)
                }}
              />
              <ArrowUpCircleIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              maior ({toneShare.higher})
            </label>
          </div>
        </div>
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <LoadingSpinner />
            <Text size="sm" className="text-gray-500 dark:text-gray-400">
              Carregando análise comparativa...
            </Text>
          </div>
        ) : (
          <>
            <HorizontalTableScroll showNavButtons={false}>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {COLUMN_DEFS.map((col) => (
                      <SortableTableHeader
                        key={col.key}
                        sortKey={col.key}
                        currentSort={sortBy}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        compact
                      >
                        {col.key === 'comissaoSemImpostos'
                          ? comissaoLiquidaColumnLabel(impostoPctRaw)
                          : col.label}
                      </SortableTableHeader>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {pageRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={COLUMN_DEFS.length}
                        className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                      >
                        {hasLoaded
                          ? 'Nenhum registro encontrado com os filtros atuais'
                          : 'Clique em Buscar para carregar a análise'}
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((row) => {
                      const metrics = rowMetrics(
                        row.totalComissao,
                        row.totalExtrato,
                        impostoPct,
                        margemPct
                      )
                      return (
                        <tr
                          key={row.dia}
                          role="button"
                          tabIndex={0}
                          aria-label={`Detalhar Comissão Icatu de ${formatDatePtBr(row.dia)}`}
                          className={`cursor-pointer transition-colors ${rowToneClass(metrics.tone)}`}
                          onClick={() =>
                            setDetailDia({ dia: row.dia, totalComissao: row.totalComissao })
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              setDetailDia({ dia: row.dia, totalComissao: row.totalComissao })
                            }
                          }}
                        >
                          <td className={CELL_CLASS}>{formatDatePtBr(row.dia)}</td>
                          <td className={CELL_RIGHT_CLASS}>
                            {renderMoney(row.totalExtrato)}
                          </td>
                          <td className={CELL_RIGHT_CLASS}>
                            {renderMoney(row.totalComissao)}
                          </td>
                          <td className={CELL_RIGHT_CLASS}>
                            {metrics.comissaoLiquida == null ? (
                              <EmptyAmount />
                            ) : (
                              renderMoney(metrics.comissaoLiquida)
                            )}
                          </td>
                          <td className={CELL_RIGHT_CLASS}>
                            <DiffValorCell
                              diff={metrics.diff}
                              tone={metrics.tone}
                              renderMoney={renderMoney}
                            />
                          </td>
                          <td className={CELL_RIGHT_CLASS}>
                            <DiffPercentCell diffPct={metrics.diffPct} tone={metrics.tone} />
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
                {sortedRows.length > 0 && (
                  <tfoot className="border-t-2 border-gray-300 dark:border-gray-600">
                    <tr className={rowToneClass(totalMetrics.tone)}>
                      <td className={FOOT_CELL_CLASS}>Total</td>
                      <td className={FOOT_CELL_RIGHT_CLASS}>{renderMoney(totalExtrato)}</td>
                      <td className={`${FOOT_CELL_RIGHT_CLASS} text-primary-700 dark:text-primary-300`}>
                        {renderMoney(totalComissao)}
                      </td>
                      <td className={FOOT_CELL_RIGHT_CLASS}>
                        {totalMetrics.comissaoLiquida == null ? (
                          <EmptyAmount />
                        ) : (
                          renderMoney(totalMetrics.comissaoLiquida)
                        )}
                      </td>
                      <td className={FOOT_CELL_RIGHT_CLASS}>
                        <DiffValorCell
                          diff={totalMetrics.diff}
                          tone={totalMetrics.tone}
                          renderMoney={renderMoney}
                        />
                      </td>
                      <td className={FOOT_CELL_RIGHT_CLASS}>
                        <DiffPercentCell
                          diffPct={totalMetrics.diffPct}
                          tone={totalMetrics.tone}
                        />
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </HorizontalTableScroll>

            <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300">
                    {sortedRows.length === 0
                      ? 'Nenhum registro'
                      : `Mostrando ${startIndex + 1} a ${endIndex} de ${sortedRows.length.toLocaleString('pt-BR')} dia${sortedRows.length !== 1 ? 's' : ''}`}
                  </Text>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <span className="whitespace-nowrap">Por página</span>
                    <Select
                      size="sm"
                      className="w-auto min-w-[5.5rem]"
                      value={String(pageSizeOption)}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const value = e.target.value
                        setPageSizeOption(
                          value === 'all' ? 'all' : (Number(value) as PageSizeOption)
                        )
                        setCurrentPage(1)
                      }}
                    >
                      {PASI_ANALISE_PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                      <option value="all">Todos</option>
                    </Select>
                  </label>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={PAGINATION_BTN_CLASS}
                    >
                      Anterior
                    </button>
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber: number
                        if (totalPages <= 5) {
                          pageNumber = i + 1
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i
                        } else {
                          pageNumber = currentPage - 2 + i
                        }
                        return (
                          <button
                            key={pageNumber}
                            type="button"
                            onClick={() => setCurrentPage(pageNumber)}
                            className={paginationPageClass(currentPage === pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        )
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={PAGINATION_BTN_CLASS}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {detailDia && (
        <AnaliseComparativaComissaoModal
          dia={detailDia.dia}
          totalComissao={detailDia.totalComissao}
          filters={appliedComissaoFilters}
          onClose={() => setDetailDia(null)}
        />
      )}
    </div>
  )
}

export default AnaliseComparativaTab
